import React, { useState, useCallback, useEffect } from 'react';
import eventBus from '../../core/EventBus';
import useIsMobile from '../../hooks/useIsMobile';

const STANDARD_SIZES = [
  { label: 'DIN A4 (21 x 29.7 cm)', w: 21, h: 29.7 },
  { label: 'DIN A3 (29.7 x 42 cm)', w: 29.7, h: 42 },
  { label: 'DIN A2 (42 x 59.4 cm)', w: 42, h: 59.4 },
  { label: 'DIN A1 (59.4 x 84.1 cm)', w: 59.4, h: 84.1 },
  { label: 'DIN A0 (84.1 x 118.9 cm)', w: 84.1, h: 118.9 },
  { label: '100 x 50 cm', w: 100, h: 50 },
  { label: '200 x 100 cm', w: 200, h: 100 },
  { label: '300 x 100 cm', w: 300, h: 100 },
  { label: '400 x 200 cm', w: 400, h: 200 },
];

const DATA_CHECK_OPTIONS = [
  { value: 'none', label: 'Kein Datencheck', days: 0 },
  { value: 'standard', label: 'Standard Datencheck', days: 3 },
  { value: 'express', label: 'Premium Datencheck', days: 1 },
];

// Finishing types per side
const FINISHING_TYPES = [
  {
    id: 'cut', label: 'Schneiden',
    desc: 'Auf das gewünschte Format zugeschnitten.',
    icon: (
      <svg viewBox="0 0 60 44" className="w-full h-full">
        <rect x="8" y="6" width="44" height="32" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="1"/>
        <line x1="8" y1="6" x2="8" y2="38" stroke="#374151" strokeWidth="2"/>
      </svg>
    ),
  },
  {
    id: 'grommets', label: 'Ösen',
    desc: 'Nickelösen werden befestigt.',
    icon: (
      <svg viewBox="0 0 60 44" className="w-full h-full">
        <rect x="8" y="6" width="44" height="32" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="1"/>
        <circle cx="16" cy="14" r="4" fill="#e5e7eb" stroke="#9ca3af" strokeWidth="1.5"/>
        <circle cx="16" cy="14" r="1.5" fill="white" stroke="#9ca3af" strokeWidth="0.5"/>
        <circle cx="44" cy="14" r="4" fill="#e5e7eb" stroke="#9ca3af" strokeWidth="1.5"/>
        <circle cx="44" cy="14" r="1.5" fill="white" stroke="#9ca3af" strokeWidth="0.5"/>
      </svg>
    ),
  },
  {
    id: 'hem', label: 'Saum',
    desc: 'Saum wird mit hoher Temperatur geschweißt.',
    icon: (
      <svg viewBox="0 0 60 44" className="w-full h-full">
        <rect x="8" y="6" width="44" height="32" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="1"/>
        <line x1="8" y1="6" x2="52" y2="6" stroke="#3b82f6" strokeWidth="1" strokeDasharray="3 2"/>
        <line x1="8" y1="12" x2="52" y2="12" stroke="#3b82f6" strokeWidth="1" strokeDasharray="3 2"/>
        <rect x="8" y="6" width="44" height="6" fill="rgba(59,130,246,0.08)"/>
      </svg>
    ),
  },
  {
    id: 'hem-grommets', label: 'Saum + Ösen',
    desc: 'Saum und Nickelösen zur Befestigung.',
    badge: 'Beliebt',
    icon: (
      <svg viewBox="0 0 60 44" className="w-full h-full">
        <rect x="8" y="6" width="44" height="32" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="1"/>
        <line x1="8" y1="6" x2="52" y2="6" stroke="#3b82f6" strokeWidth="1" strokeDasharray="3 2"/>
        <line x1="8" y1="12" x2="52" y2="12" stroke="#3b82f6" strokeWidth="1" strokeDasharray="3 2"/>
        <circle cx="16" cy="9" r="3" fill="#e5e7eb" stroke="#9ca3af" strokeWidth="1"/>
        <circle cx="16" cy="9" r="1.2" fill="white"/>
        <circle cx="44" cy="9" r="3" fill="#e5e7eb" stroke="#9ca3af" strokeWidth="1"/>
        <circle cx="44" cy="9" r="1.2" fill="white"/>
      </svg>
    ),
  },
  {
    id: 'hohlsaum', label: 'Hohlsaum',
    desc: 'Geschweißter Tunnel für Stangen.',
    icon: (
      <svg viewBox="0 0 60 44" className="w-full h-full">
        <rect x="8" y="6" width="44" height="32" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="1"/>
        <ellipse cx="8" cy="10" rx="3" ry="6" fill="#e5e7eb" stroke="#9ca3af" strokeWidth="1"/>
        <line x1="8" y1="4" x2="52" y2="4" stroke="#7c3aed" strokeWidth="1.5" strokeDasharray="4 2"/>
        <line x1="8" y1="16" x2="52" y2="16" stroke="#7c3aed" strokeWidth="1.5" strokeDasharray="4 2"/>
      </svg>
    ),
  },
  {
    id: 'klett', label: 'Klettverschluss',
    desc: 'Klettverschluss zur Befestigung.',
    icon: (
      <svg viewBox="0 0 60 44" className="w-full h-full">
        <rect x="8" y="6" width="44" height="32" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="1"/>
        <rect x="8" y="6" width="6" height="32" fill="#fbbf24" opacity="0.3" stroke="#d97706" strokeWidth="0.5"/>
        <line x1="11" y1="10" x2="11" y2="14" stroke="#d97706" strokeWidth="0.8"/>
        <line x1="11" y1="18" x2="11" y2="22" stroke="#d97706" strokeWidth="0.8"/>
        <line x1="11" y1="26" x2="11" y2="30" stroke="#d97706" strokeWidth="0.8"/>
      </svg>
    ),
  },
  {
    id: 'keder', label: 'Keder (rund)',
    desc: 'Silikonkeder (7 mm) für Rahmen.',
    icon: (
      <svg viewBox="0 0 60 44" className="w-full h-full">
        <rect x="8" y="6" width="44" height="32" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="1"/>
        <line x1="6" y1="6" x2="6" y2="38" stroke="#6b7280" strokeWidth="3" strokeLinecap="round"/>
      </svg>
    ),
  },
];

const GROMMET_SPACINGS = [
  { id: 'corners', label: 'In jeder Ecke' },
  { id: 'every20', label: 'Um die 20 cm' },
  { id: 'every25', label: 'Um die 25 cm' },
  { id: 'every30', label: 'Um die 30 cm' },
  { id: 'every40', label: 'Um die 40 cm' },
  { id: 'every50', label: 'Um die 50 cm' },
];

const SIDE_LABELS = { top: 'Oben', bottom: 'Unten', left: 'Links', right: 'Rechts' };

// ---- Sub-Components ----

function ModeSelector({ mode, onChange, allowPerSide = true }) {
  const modes = [
    { id: 'all', label: 'Für alle Seiten gleich', desc: 'Alle Seiten erhalten die selbe Verarbeitung.',
      icon: <svg viewBox="0 0 48 40" className="w-10 h-8 text-blue-500"><rect x="10" y="6" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" rx="1"/><path d="M24 3v3M24 37v-3M7 22h3M41 22h-3" stroke="currentColor" strokeWidth="1.2" markerEnd="url(#arr)"/></svg>,
      disabled: false,
    },
    { id: 'perSide', label: 'Abweichend pro Seite', desc: allowPerSide ? 'Für jede Seite einzeln wählen.' : 'Für diesen Artikel nicht verfügbar.',
      icon: <svg viewBox="0 0 48 40" className="w-10 h-8 text-blue-500"><rect x="10" y="6" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" rx="1"/><path d="M6 22h4" stroke="currentColor" strokeWidth="1.5"/></svg>,
      disabled: !allowPerSide,
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 mb-4">
      {modes.map((m) => (
        <button
          key={m.id}
          onClick={() => !m.disabled && onChange(m.id)}
          title={m.disabled ? 'Diese Option ist für diesen Artikel nicht verfügbar' : ''}
          className={`relative p-3 rounded-lg border-2 text-left transition-all ${
            m.disabled
              ? 'border-gray-100 bg-gray-50 opacity-40 cursor-not-allowed dark:border-gray-700 dark:bg-gray-800'
              : mode === m.id
                ? 'border-blue-500 bg-blue-50 shadow-sm dark:bg-blue-500/20'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-700'
          }`}
        >
          {m.disabled && (
            <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-[7px] font-bold text-white bg-gray-400 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap">Nicht verfügbar</span>
          )}
          <div className="flex justify-center mb-2">{m.icon}</div>
          <p className="text-[11px] font-semibold text-gray-800 dark:text-gray-100 leading-tight">{m.label}</p>
          <p className="text-[10px] text-gray-400 dark:text-gray-400 mt-0.5 leading-snug">{m.desc}</p>
        </button>
      ))}
    </div>
  );
}

function FinishingTypeCards({ value, onChange, allowedTypes }) {
  const hasFilter = allowedTypes && allowedTypes.length > 0;
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {FINISHING_TYPES.map((ft) => {
        const isAllowed = !hasFilter || allowedTypes.includes(ft.id);
        return (
          <button
            key={ft.id}
            onClick={() => isAllowed && onChange(ft.id)}
            title={!isAllowed ? 'Diese Verarbeitung ist für diesen Artikel nicht verfügbar' : ''}
            className={`relative p-1.5 rounded-lg border-2 transition-all text-center group ${
              !isAllowed
                ? 'border-gray-100 bg-gray-50 opacity-40 cursor-not-allowed dark:border-gray-700 dark:bg-gray-800'
                : value === ft.id
                  ? 'border-blue-500 bg-blue-50 shadow-sm dark:bg-blue-500/20'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-700'
            }`}
          >
            {ft.badge && isAllowed && (
              <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-[8px] font-bold text-white bg-green-500 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap">{ft.badge}</span>
            )}
            {!isAllowed && (
              <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-[7px] font-bold text-white bg-gray-400 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap">Nicht verfügbar</span>
            )}
            <div className="w-full h-8 mb-1">{ft.icon}</div>
            <p className="text-[10px] font-medium text-gray-700 dark:text-gray-200 leading-tight">{ft.label}</p>
          </button>
        );
      })}
    </div>
  );
}

function FinishingOptions({ sideConfig, onChange, isAllMode = false, urlPricing }) {
  const t = sideConfig.type;

  // Filter sub-options based on product config
  const allowedHem = urlPricing?.allowedHemWidths;
  const hemWidths = (allowedHem && allowedHem.length > 0) ? [3, 4, 5].filter(v => allowedHem.includes(v)) : [3, 4, 5];
  const allowedGrommet = urlPricing?.allowedGrommetSizes;
  const grommetSizes = (allowedGrommet && allowedGrommet.length > 0)
    ? [{ v: 11, l: '11 mm' }, { v: 13, l: '13 mm' }].filter(g => allowedGrommet.includes(g.v))
    : [{ v: 11, l: '11 mm' }, { v: 13, l: '13 mm' }];
  const allowedSpacing = urlPricing?.allowedGrommetSpacings;
  const spacings = (allowedSpacing && allowedSpacing.length > 0) ? GROMMET_SPACINGS.filter(gs => allowedSpacing.includes(gs.id)) : GROMMET_SPACINGS;

  if (t === 'grommets' || t === 'hem-grommets') {
    return (
      <div className="mt-2 space-y-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 animate-fadeIn">
        {(t === 'hem' || t === 'hem-grommets') && hemWidths.length > 1 && (
          <div>
            <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 block">Saumbreite</label>
            <div className="flex gap-1">
              {hemWidths.map((v) => (
                <button key={v} onClick={() => onChange({ ...sideConfig, saumCm: v })}
                  className={`flex-1 text-[10px] py-1 rounded border transition-colors ${sideConfig.saumCm === v ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 font-semibold' : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'}`}
                >{v} cm</button>
              ))}
            </div>
          </div>
        )}
        {spacings.length > 1 && (
        <div>
          <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 block">Ösenabstand</label>
          <div className="grid grid-cols-3 gap-1">
            {spacings.map((gs) => (
              <button key={gs.id} onClick={() => onChange({ ...sideConfig, grommetMode: gs.id })}
                className={`text-[9px] py-1 px-1 rounded border transition-colors leading-tight ${sideConfig.grommetMode === gs.id ? 'border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white font-semibold' : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'}`}
              >{gs.label}</button>
            ))}
          </div>
        </div>
        )}
        {grommetSizes.length > 1 && (
        <div>
          <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 block">Ösen-Größe</label>
          <div className="flex gap-1">
            {grommetSizes.map(({ v, l }) => (
              <button key={v} onClick={() => onChange({ ...sideConfig, grommetSize: v })}
                className={`flex-1 text-[10px] py-1 rounded border transition-colors ${sideConfig.grommetSize === v ? 'border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white font-semibold' : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'}`}
              >{l}</button>
            ))}
          </div>
        </div>
        )}
      </div>
    );
  }

  if (t === 'hem') {
    return hemWidths.length > 1 ? (
      <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 animate-fadeIn">
        <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 block">Saumbreite</label>
        <div className="flex gap-1">
          {hemWidths.map((v) => (
            <button key={v} onClick={() => onChange({ ...sideConfig, saumCm: v })}
              className={`flex-1 text-[10px] py-1 rounded border transition-colors ${sideConfig.saumCm === v ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 font-semibold' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
            >{v} cm</button>
          ))}
        </div>
      </div>
    ) : null;
  }

  if (t === 'hohlsaum') {
    const dir = sideConfig.hohlsaumDir || 'topBottom';
    return (
      <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 animate-fadeIn space-y-2">
        {isAllMode && (
          <div>
            <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 block">Richtung</label>
            <div className="flex gap-1">
              {[{ v: 'topBottom', l: 'Oben + Unten' }, { v: 'leftRight', l: 'Links + Rechts' }].map(({ v, l }) => (
                <button key={v} onClick={() => onChange({ ...sideConfig, hohlsaumDir: v })}
                  className={`flex-1 text-[10px] py-1 rounded border transition-colors ${dir === v ? 'border-purple-500 bg-purple-50 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 font-semibold' : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'}`}
                >{l}</button>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 block">Hohlsaumbreite (min. 4 cm)</label>
          <div className="flex items-center gap-2">
            <input
              type="number" min="4" max="20" step="1"
              value={sideConfig.hohlsaumCm || 5}
              onChange={(e) => onChange({ ...sideConfig, hohlsaumCm: Math.max(4, Math.min(20, Number(e.target.value))) })}
              className="selenny-input w-20"
            />
            <span className="text-[10px] text-gray-400">cm</span>
            <input
              type="range" min="4" max="20" step="1"
              value={sideConfig.hohlsaumCm || 5}
              onChange={(e) => onChange({ ...sideConfig, hohlsaumCm: Number(e.target.value) })}
              className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 block">Enden geschlossen?</label>
          <p className="text-[9px] text-gray-400 mb-1">
            {isAllMode
              ? (dir === 'leftRight' ? 'Oben und unten zunähen (z.B. für Pfostenmontage)' : 'Links und rechts zunähen (z.B. für Pfostenmontage)')
              : 'Tunnel an beiden Enden zunähen (z.B. für Pfostenmontage)'
            }
          </p>
          <div className="flex gap-1">
            {[{ v: false, l: 'Offen' }, { v: true, l: 'Geschlossen' }].map(({ v, l }) => (
              <button key={String(v)} onClick={() => onChange({ ...sideConfig, hohlsaumClosed: v })}
                className={`flex-1 text-[10px] py-1 rounded border transition-colors ${(!!sideConfig.hohlsaumClosed) === v ? 'border-purple-500 bg-purple-50 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 font-semibold' : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'}`}
              >{l}</button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (t === 'klett') {
    return (
      <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 animate-fadeIn">
        <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 block">Position</label>
        <div className="flex gap-1">
          {[{ v: 'front', l: 'Vorderseite' }, { v: 'back', l: 'Rückseite' }].map(({ v, l }) => (
            <button key={v} onClick={() => onChange({ ...sideConfig, klettPos: v })}
              className={`flex-1 text-[10px] py-1 rounded border transition-colors ${sideConfig.klettPos === v ? 'border-amber-500 bg-amber-50 text-amber-700 font-semibold' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
            >{l}</button>
          ))}
        </div>
        <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 block mt-1.5">Farbe</label>
        <div className="flex gap-1">
          {[{ v: 'white', l: 'Weiß' }, { v: 'black', l: 'Schwarz' }].map(({ v, l }) => (
            <button key={v} onClick={() => onChange({ ...sideConfig, klettColor: v })}
              className={`flex-1 text-[10px] py-1 rounded border transition-colors ${(sideConfig.klettColor || 'white') === v ? 'border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white font-semibold' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
            >{l}</button>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

// ---- Main Component ----

function ProductPanel({
  widthCm, heightCm, productName,
  onDimensionChange, onProductNameChange, onSave, onSaveAsNew, onAddToCart, onPricingChange, designId,
  isEmbedded = false,
  canSave = true,
  isAdmin = false,
  bleedCm = 1, hemCm = 0,
  grommets = { enabled: false, mode: 'corners', customSpacingCm: 40, radiusMm: 11 },
  onBleedChange, onHemChange, onGrommetsChange,
  hohlsaum = { top: 0, bottom: 0, left: 0, right: 0 }, onHohlsaumChange,
  finishing, onFinishingChange,
  productId = null,
  shopUrl = '',
  urlPricing = null, // { pricePerM2, minPrice, shippingCost } from URL params
  hideSizes = false,
}) {
  const isMobile = useIsMobile();
  const [localWidth, setLocalWidth] = useState(String(widthCm));
  const [localHeight, setLocalHeight] = useState(String(heightCm));
  const [dataCheck, setDataCheck] = useState('none');
  const [quantity, setQuantity] = useState(1);
  const [showFinishing, setShowFinishing] = useState(false);
  const [activeSide, setActiveSide] = useState(null); // which side is expanded in perSide mode
  const [fixedPrice, setFixedPrice] = useState(null); // active fixed price from standard size selection

  // API-based pricing state
  const [printConfig, setPrintConfig] = useState(null); // loaded from shop API
  const [selectedMaterialId, setSelectedMaterialId] = useState(null);
  const [selectedFinishingKeys, setSelectedFinishingKeys] = useState([]);
  const [apiPricing, setApiPricing] = useState(null); // result from calculate API
  const [pricingLoading, setPricingLoading] = useState(!!productId && !!shopUrl); // start loading if API pricing expected
  const calcTimerRef = React.useRef(null);

  // Load print config from shop API on mount
  // Skip if central API already provides supplier-based pricing (urlPricing.suppliers)
  const hasCentralPricing = !!(urlPricing?.suppliers && urlPricing.suppliers.length > 0);
  useEffect(() => {
    if (!productId || !shopUrl || hasCentralPricing) { if (hasCentralPricing) setPricingLoading(false); return; }
    setPricingLoading(true);
    fetch(`${shopUrl}/api/v1/print-config/${productId}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(data => {
        setPrintConfig(data);
        // Set default material
        const defaultMat = data.materials?.find(m => m.is_default) || data.materials?.[0];
        if (defaultMat) setSelectedMaterialId(defaultMat.id);
        // Apply size limits if current dimensions are outside bounds
        if (data.min_width_cm && widthCm < data.min_width_cm) onDimensionChange(data.min_width_cm, heightCm);
        if (data.min_height_cm && heightCm < data.min_height_cm) onDimensionChange(widthCm, data.min_height_cm);
      })
      .catch(() => { setPricingLoading(false); /* Fallback to hardcoded pricing */ });
  }, [productId, shopUrl, hasCentralPricing]);

  // Debounced price calculation via shop API
  // Skip if central API provides supplier-based pricing
  useEffect(() => {
    if (!productId || !shopUrl || !printConfig || hasCentralPricing) return;
    clearTimeout(calcTimerRef.current);
    calcTimerRef.current = setTimeout(() => {
      setPricingLoading(true);
      fetch(`${shopUrl}/api/v1/print-config/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productId,
          width_cm: widthCm,
          height_cm: heightCm,
          material_id: selectedMaterialId,
          finishing: selectedFinishingKeys,
          quantity: quantity,
        }),
      })
        .then(r => r.ok ? r.json() : Promise.reject(r))
        .then(data => { setApiPricing(data); setPricingLoading(false); })
        .catch(() => { setApiPricing(null); setPricingLoading(false); });
    }, 300);
    return () => clearTimeout(calcTimerRef.current);
  }, [productId, shopUrl, printConfig, hasCentralPricing, widthCm, heightCm, selectedMaterialId, selectedFinishingKeys, quantity]);

  // Force mode back to 'all' if perSide is not allowed
  useEffect(() => {
    if (urlPricing && urlPricing.allowPerSideFinishing === false && finishing?.mode === 'perSide') {
      onFinishingChange?.({ ...finishing, mode: 'all' });
    }
  }, [urlPricing?.allowPerSideFinishing]);

  // Sync local dimensions when props change (e.g. after loading a saved design)
  useEffect(() => { setLocalWidth(String(widthCm)); }, [widthCm]);
  useEffect(() => { setLocalHeight(String(heightCm)); }, [heightCm]);

  // Close finishing panel when clicking on the canvas stage or when toolbar dialogs close
  useEffect(() => {
    const unsub1 = eventBus.on('stage:click', () => { setShowFinishing(false); setActiveFlyout(null); });
    const unsub2 = eventBus.on('toolbar:close-dialogs', () => { setShowFinishing(false); setActiveFlyout(null); });
    return () => { unsub1(); unsub2(); };
  }, []);

  // Listen for assistant panel-open requests
  useEffect(() => {
    const unsub = eventBus.on('assistant:open-panel', (data) => {
      if (data?.panel === 'finishing') {
        setShowFinishing(true);
      }
    });
    return () => unsub();
  }, []);

  // Pricing priority: 1) Central API suppliers, 2) Shop API, 3) Fallback
  const useApiPricing = !!(printConfig && apiPricing && !hasCentralPricing);
  const areaCm2 = widthCm * heightCm;
  const areaM2 = areaCm2 / 10000;

  // Stück-Modus Detection: first supplier signals piece-based pricing via tierUnit='stueck'
  // In piece-mode, priceTiers' fromM2/toM2 fields represent quantity ranges, and pricePerM2 is the per-piece price.
  const isStueckMode = !!(urlPricing?.suppliers && urlPricing.suppliers.length > 0 && urlPricing.suppliers[0]?.tierUnit === 'stueck');
  let unitPricePerPiece = 0;

  let basePrice, finishingTotal, shippingCost, setupCost, oversizeCost, totalPrice, packagingCost = 0, supplierCode = '';
  const isFixedPrice = fixedPrice != null && fixedPrice > 0;
  if (isFixedPrice) {
    // Fixed price from standard size selection — overrides all calculation
    basePrice = fixedPrice;
    finishingTotal = 0;
    shippingCost = urlPricing?.shippingTiers?.length > 0
      ? (() => { const ss = Math.min(widthCm, heightCm); const sorted = [...urlPricing.shippingTiers].sort((a, b) => (a.maxShortSide || 9999) - (b.maxShortSide || 9999)); const tier = sorted.find(t => ss <= (t.maxShortSide || 9999)) || sorted[sorted.length - 1]; return tier?.shippingCost || 0; })()
      : 0;
    setupCost = 0;
    oversizeCost = 0;
    packagingCost = 0;
    totalPrice = basePrice * quantity + shippingCost;
  } else if (isStueckMode) {
    // Stück-Modus: Tier-Lookup über Menge statt Fläche. Felder fromM2/toM2 = Stückbereich, pricePerM2 = Preis pro Stück.
    // Supplier-Selektion: ersten Supplier nehmen (Stück-Produkte haben üblicherweise einen Lieferanten — keine Größen-Sortierung sinnvoll).
    const supplier = urlPricing.suppliers[0];
    supplierCode = supplier.shortCode || supplier.name?.substring(0, 3).toUpperCase() || '';

    // EK pro Stück aus Tier ermitteln
    let ekPerPiece = supplier.pricePerM2 || 0;
    if (supplier.priceTiers && supplier.priceTiers.length > 0) {
      const sortedTiers = [...supplier.priceTiers].sort((a, b) => (a.fromM2 || 0) - (b.fromM2 || 0));
      for (const tier of sortedTiers) {
        if (quantity >= (tier.fromM2 || 0) && quantity <= (tier.toM2 || 999999)) {
          ekPerPiece = tier.pricePerM2 || 0;
          break;
        }
      }
      // Fallback: falls Menge über alle Tiers hinausgeht, nimm den letzten Tier
      if (ekPerPiece === 0 && quantity > 0) {
        const last = sortedTiers[sortedTiers.length - 1];
        if (last && quantity >= (last.fromM2 || 0)) ekPerPiece = last.pricePerM2 || 0;
      }
    }

    // Markup anwenden (gleiche Logik wie m²-Modus)
    const hasSupplierMarkup = (supplier.markupPercent != null && supplier.markupPercent > 0) || (supplier.markupFixed != null && supplier.markupFixed > 0);
    const markupPct = hasSupplierMarkup ? (supplier.markupPercent || 0) : (urlPricing.markupPercent || 0);
    const markupFix = hasSupplierMarkup ? (supplier.markupFixed || 0) : (urlPricing.markupFixed || 0);
    const vkPerPiece = markupPct > 0 ? (ekPerPiece * (markupPct / 100)) + markupFix : ekPerPiece + markupFix;
    unitPricePerPiece = vkPerPiece;

    // Mindestpreis greift auf Stückpreis-Ebene
    const minPrice = supplier.minPrice || 0;
    basePrice = (minPrice > 0 && vkPerPiece < minPrice) ? minPrice : vkPerPiece;
    finishingTotal = 0;
    shippingCost = supplier.shippingCost || 0;
    setupCost = supplier.setupCost || 0;
    oversizeCost = 0;
    packagingCost = supplier.packagingCost || 0;
    // Versand + Setup einmalig, nicht pro Stück. Verpackung pro Stück (analog zu Praxis bei Stückartikeln).
    totalPrice = (basePrice + packagingCost) * quantity + setupCost + shippingCost;
  } else if (urlPricing?.suppliers && urlPricing.suppliers.length > 0) {
    // Supplier-based pricing with tiers + markup from Selenny Admin
    const shortestSide = Math.min(widthCm, heightCm);
    const sortedSuppliers = [...urlPricing.suppliers].sort((a, b) => (a.maxShortSide || 9999) - (b.maxShortSide || 9999));
    const supplier = sortedSuppliers.find(s => shortestSide <= (s.maxShortSide || 9999)) || sortedSuppliers[sortedSuppliers.length - 1];
    supplierCode = supplier.shortCode || supplier.name?.substring(0, 3).toUpperCase() || '';

    // Find EK from price tiers
    let ekPerM2 = supplier.pricePerM2 || 0;
    if (supplier.priceTiers && supplier.priceTiers.length > 0) {
      const sortedTiers = [...supplier.priceTiers].sort((a, b) => (a.fromM2 || 0) - (b.fromM2 || 0));
      for (const tier of sortedTiers) {
        if (areaM2 >= (tier.fromM2 || 0) && areaM2 <= (tier.toM2 || 9999)) {
          ekPerM2 = tier.pricePerM2 || 0;
          break;
        }
      }
    }

    // Apply markup: VK = EK × (Aufschlag/100) + fixedPerM2
    // 200% = doppelter EK, 300% = dreifacher EK, 100% = kein Aufschlag
    // Per-supplier markup eliminates global product markup
    const hasSupplierMarkup = (supplier.markupPercent != null && supplier.markupPercent > 0) || (supplier.markupFixed != null && supplier.markupFixed > 0);
    const markupPct = hasSupplierMarkup ? (supplier.markupPercent || 0) : (urlPricing.markupPercent || 0);
    const markupFix = hasSupplierMarkup ? (supplier.markupFixed || 0) : (urlPricing.markupFixed || 0);
    const vkPerM2 = markupPct > 0 ? (ekPerM2 * (markupPct / 100)) + markupFix : ekPerM2 + markupFix;

    const minPrice = supplier.minPrice || 0;
    const calculatedPrice = areaM2 * vkPerM2;
    if (minPrice > 0 && calculatedPrice < minPrice) {
      // minPrice as base + proportional surcharge for area beyond minimum
      const minAreaM2 = vkPerM2 > 0 ? (minPrice / vkPerM2) : 0;
      const extraArea = Math.max(0, areaM2 - minAreaM2);
      basePrice = minPrice + extraArea * vkPerM2;
    } else {
      basePrice = calculatedPrice;
    }
    finishingTotal = 0;
    // Shipping: product-level tiers take priority, then supplier shippingCost
    if (urlPricing.shippingTiers && urlPricing.shippingTiers.length > 0) {
      const sortedShipping = [...urlPricing.shippingTiers].sort((a, b) => (a.maxShortSide || 9999) - (b.maxShortSide || 9999));
      const shippingTier = sortedShipping.find(t => shortestSide <= (t.maxShortSide || 9999)) || sortedShipping[sortedShipping.length - 1];
      shippingCost = shippingTier.shippingCost || 0;
    } else {
      shippingCost = supplier.shippingCost || 0;
    }
    setupCost = supplier.setupCost || 0;
    oversizeCost = 0;
    packagingCost = supplier.packagingCost || 0;
    totalPrice = (basePrice + setupCost + oversizeCost + packagingCost) * quantity + shippingCost;
  } else if (useApiPricing) {
    basePrice = apiPricing.vk_base;
    finishingTotal = apiPricing.finishing_total || 0;
    shippingCost = apiPricing.shipping || 0;
    setupCost = apiPricing.setup || 0;
    oversizeCost = apiPricing.oversize || 0;
    totalPrice = apiPricing.vk_total;
  } else {
    const pricePerM2 = urlPricing?.pricePerM2 || 25.0;
    const minPrice = urlPricing?.minPrice || 5.0;
    const calculatedPriceFb = areaM2 * pricePerM2;
    if (minPrice > 0 && calculatedPriceFb < minPrice) {
      const minAreaFb = pricePerM2 > 0 ? (minPrice / pricePerM2) : 0;
      const extraAreaFb = Math.max(0, areaM2 - minAreaFb);
      basePrice = minPrice + extraAreaFb * pricePerM2;
    } else {
      basePrice = calculatedPriceFb;
    }
    finishingTotal = 0;
    shippingCost = urlPricing?.shippingCost || 0;
    setupCost = urlPricing?.setupCost || 0;
    const oversizeThreshold = urlPricing?.oversizeThreshold || 100;
    const oversizeSurcharge = urlPricing?.oversizeSurcharge || 0;
    const shortestSide = Math.min(widthCm, heightCm);
    oversizeCost = (oversizeSurcharge > 0 && shortestSide > oversizeThreshold) ? oversizeSurcharge : 0;
    packagingCost = urlPricing?.packagingCost || 0;
    totalPrice = (basePrice + setupCost + oversizeCost + packagingCost) * quantity + shippingCost;
  }

  // Keep parent informed about current pricing (for overlay add-to-cart)
  // Note: dynamicGrandTotal and dynamicDataCheckSurcharge are computed in the render body below
  const selectedMaterial = printConfig?.materials?.find(m => m.id === selectedMaterialId);
  const pricingUpdateRef = React.useRef(null);
  pricingUpdateRef.current = { basePrice, dataCheck, quantity, widthCm, heightCm, selectedMaterialId, selectedMaterial, selectedFinishingKeys, finishingTotal, shippingCost, areaM2 };

  // Use API standard sizes if available: 1) Central API, 2) Shop API, 3) built-in defaults (only if no API data loaded)
  const hasCentralProduct = !!(urlPricing?.suppliers && urlPricing.suppliers.length > 0);
  const mapSize = (s) => {
    const w = Number(s.w || s.width || 0);
    const h = Number(s.h || s.height || 0);
    return { label: s.label || `${w}\u00D7${h}cm`, w, h, fixedPrice: s.fixedPrice || s.fixed_price || null };
  };
  const activeSizes = (urlPricing?.standardSizes?.length > 0)
    ? urlPricing.standardSizes.map(mapSize)
    : (printConfig?.standard_sizes?.length > 0)
      ? printConfig.standard_sizes.map(mapSize)
      : hasCentralProduct ? [] : STANDARD_SIZES;

  // Clear fixed price when user manually changes dimensions
  const clearFixedPriceOnManualChange = useCallback((w, h) => {
    const match = activeSizes.find(s => s.w === w && s.h === h && s.fixedPrice);
    if (!match) setFixedPrice(null);
  }, [activeSizes]);

  // Beim Mount + wenn activeSizes nachgeladen werden: prüfen ob die aktuelle Größe
  // einer Standardgröße mit Festpreis entspricht → fixedPrice initial setzen.
  // Sonst wird beim Öffnen der per-m²-Preis statt des Festpreises angezeigt.
  useEffect(() => {
    if (!activeSizes || activeSizes.length === 0) return;
    const w = Number(parseFloat(localWidth) || widthCm);
    const h = Number(parseFloat(localHeight) || heightCm);
    if (!(w > 0) || !(h > 0)) return;
    const match = activeSizes.find(s => Number(s.w) === w && Number(s.h) === h && s.fixedPrice);
    if (match && match.fixedPrice !== fixedPrice) {
      setFixedPrice(match.fixedPrice);
    }
  }, [activeSizes, localWidth, localHeight, widthCm, heightCm, fixedPrice]);

  const applyDimensions = useCallback(() => {
    const minW = printConfig?.min_width_cm || urlPricing?.minWidth || 1;
    const maxW = printConfig?.max_width_cm || urlPricing?.maxWidth || 2000;
    const minH = printConfig?.min_height_cm || urlPricing?.minHeight || 1;
    const maxH = printConfig?.max_height_cm || urlPricing?.maxHeight || 2000;
    const w = Math.max(minW, Math.min(maxW, parseFloat(localWidth) || minW));
    const h = Math.max(minH, Math.min(maxH, parseFloat(localHeight) || minH));
    setLocalWidth(String(w));
    setLocalHeight(String(h));
    clearFixedPriceOnManualChange(w, h);
    onDimensionChange(w, h);
  }, [localWidth, localHeight, onDimensionChange, printConfig, clearFixedPriceOnManualChange]);

  // Auto-apply dimensions on every input change (debounced 500ms)
  const dimTimerRef = React.useRef(null);
  useEffect(() => {
    const w = parseFloat(localWidth);
    const h = parseFloat(localHeight);
    if (w > 0 && h > 0) {
      clearTimeout(dimTimerRef.current);
      dimTimerRef.current = setTimeout(() => {
        onDimensionChange(w, h);
      }, 500);
    }
    return () => clearTimeout(dimTimerRef.current);
  }, [localWidth, localHeight, onDimensionChange]);

  const handleStandardSize = (e) => {
    const idx = Number(e.target.value);
    if (idx < 0) return;
    const size = activeSizes[idx];
    setLocalWidth(String(size.w));
    setLocalHeight(String(size.h));
    setFixedPrice(size.fixedPrice || null);
    onDimensionChange(size.w, size.h);
  };

  // Summary
  const getSummary = () => {
    if (!finishing) return '';
    const parts = [];
    if (bleedCm > 0) parts.push(`Beschnitt ${bleedCm} cm`);
    if (finishing.mode === 'all') {
      const t = FINISHING_TYPES.find(ft => ft.id === finishing.all.type);
      if (t) parts.push(t.label);
    } else {
      ['top', 'bottom', 'left', 'right'].forEach(s => {
        const t = FINISHING_TYPES.find(ft => ft.id === finishing[s].type);
        if (t && t.id !== 'cut') parts.push(`${SIDE_LABELS[s]}: ${t.label}`);
      });
    }
    return parts.join(' / ');
  };

  const updateSideConfig = (side, newConfig) => {
    const updated = { ...finishing, [side]: newConfig };
    onFinishingChange?.(updated);
  };

  const updateAllConfig = (newConfig) => {
    const updated = { ...finishing, all: newConfig };
    onFinishingChange?.(updated);
  };

  // Flyout state: only one open at a time
  const [activeFlyout, setActiveFlyout] = useState('size'); // 'size' | 'finishing' | 'order' | null — default open
  const toggleFlyout = (name) => { setActiveFlyout(prev => prev === name ? null : name); if (name !== 'finishing') setShowFinishing(false); };

  // Bestelloptionen-Panel ausgeblendet (Menge/Datencheck/Lieferzeit kommen via Shop-Produktseite)
  // Zum Wiederaktivieren: HIDE_ORDER_OPTIONS = false
  const HIDE_ORDER_OPTIONS = true;

  // Im Stückmodus den Größen-Flyout durch Bestelloptionen ersetzen (Menge dort)
  useEffect(() => {
    if (HIDE_ORDER_OPTIONS) return;
    if (isStueckMode && activeFlyout === 'size') setActiveFlyout('order');
  }, [isStueckMode]);

  // Use API datacheck options if available, then URL params, then defaults
  const defaultStandardPrice = urlPricing?.datacheckStandard ?? 5.0;
  const defaultExpressPrice = urlPricing?.datacheckExpress ?? 15.0;
  const activeDataCheckOptions = (printConfig?.datacheck_options?.length > 0)
    ? printConfig.datacheck_options.map(o => ({ value: o.key, label: o.label, days: o.days, price: o.price }))
    : DATA_CHECK_OPTIONS.map(o => ({ ...o, price: o.value === 'express' ? defaultExpressPrice : o.value === 'standard' ? defaultStandardPrice : 0 }));

  const activeBaseDeliveryDays = urlPricing?.deliveryDays || printConfig?.base_delivery_days || '3';
  const activeDataCheckOption = activeDataCheckOptions.find(o => o.value === dataCheck) || activeDataCheckOptions[0];
  const dynamicDataCheckSurcharge = activeDataCheckOption?.price ?? (dataCheck === 'express' ? 15.0 : dataCheck === 'standard' ? 5.0 : 0);
  // If delivery is free text (e.g. "3-4 Werktage"), use as-is; if number, add datacheck days
  const isNumericDelivery = !isNaN(Number(activeBaseDeliveryDays));
  const dynamicDeliveryDays = isNumericDelivery
    ? Number(activeBaseDeliveryDays) + (activeDataCheckOption?.days || 0)
    : activeBaseDeliveryDays;

  // Recalculate grand total with dynamic datacheck
  const dynamicGrandTotal = useApiPricing
    ? (totalPrice + dynamicDataCheckSurcharge * quantity)
    : isStueckMode
      ? (basePrice + (packagingCost || 0) + dynamicDataCheckSurcharge) * quantity + (setupCost || 0) + (shippingCost || 0)
      : (basePrice + (setupCost || 0) + (oversizeCost || 0) + (packagingCost || 0) + dynamicDataCheckSurcharge) * quantity + (shippingCost || 0);

  // Update parent pricing ref
  if (onPricingChange) {
    onPricingChange.current = {
      totalPrice: dynamicGrandTotal, basePrice, dataCheckSurcharge: dynamicDataCheckSurcharge, dataCheck, quantity, widthCm, heightCm,
      materialId: selectedMaterialId, materialName: selectedMaterial?.name || null,
      finishingKeys: selectedFinishingKeys.length > 0 ? selectedFinishingKeys : null,
      finishingTotal: finishingTotal || null, shippingCost: shippingCost || null, areaM2,
      tierUnit: isStueckMode ? 'stueck' : 'm2',
      unitPricePerPiece: isStueckMode ? unitPricePerPiece : null,
    };
  }

  // Count active order options for badge
  const orderOptionCount = [
    printConfig?.materials?.length > 1 ? 1 : 0,
    selectedFinishingKeys.length,
    dataCheck !== 'standard' ? 1 : 0,
    quantity > 1 ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  return (
    <div className="p-3 overflow-y-auto flex-1 relative">
      {/* Produktname */}
      <div className="mb-3">
        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Produktname</label>
        <input type="text" value={productName} onChange={(e) => onProductNameChange(e.target.value)} placeholder="z.B. Werbebanner" className="selenny-input" />
      </div>

      {/* === Flyout-Buttons === */}

      {/* Größe & Format — im Stückmodus ausgeblendet (Menge wird in Bestelloptionen gesteuert) */}
      {!isStueckMode && (
      <div className="mb-2">
        <button
          data-guide-id="panel-dimensions"
          onClick={() => toggleFlyout('size')}
          className={`w-full flex items-center justify-between text-xs px-3 py-2 rounded-lg border-2 transition-all ${
            activeFlyout === 'size' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 shadow-sm' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 3H3v18h18V3z"/><path d="M9 3v18"/><path d="M3 9h18"/></svg>
            <span className="font-semibold">Größe & Format</span>
          </span>
          <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500">{widthCm} x {heightCm} cm</span>
        </button>
      </div>
      )}

      {/* Verarbeitung */}
      <div className="mb-2">
        <button
          data-guide-id="btn-finishing"
          onClick={() => { toggleFlyout('finishing'); setShowFinishing(activeFlyout !== 'finishing'); }}
          className={`w-full flex items-center justify-between text-xs px-3 py-2 rounded-lg border-2 transition-all ${
            activeFlyout === 'finishing' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 shadow-sm' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"/></svg>
            <span className="font-semibold">Verarbeitung</span>
          </span>
          <svg className={`w-3.5 h-3.5 transition-transform ${activeFlyout === 'finishing' ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
        </button>
        {activeFlyout !== 'finishing' && getSummary() && (
          <p className="text-[10px] text-gray-400 mt-0.5 px-1 truncate">{getSummary()}</p>
        )}
      </div>

      {/* Bestelloptionen — ausgeblendet (HIDE_ORDER_OPTIONS), Auswahl erfolgt auf Shop-Produktseite */}
      {!HIDE_ORDER_OPTIONS && (
      <div className="mb-3">
        <button
          onClick={() => toggleFlyout('order')}
          className={`w-full flex items-center justify-between text-xs px-3 py-2 rounded-lg border-2 transition-all ${
            activeFlyout === 'order' ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 shadow-sm' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            <span className="font-semibold">Bestelloptionen</span>
          </span>
          <span className="flex items-center gap-1.5">
            {orderOptionCount > 0 && (
              <span className="bg-green-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{orderOptionCount}</span>
            )}
            <svg className={`w-3.5 h-3.5 transition-transform ${activeFlyout === 'order' ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </span>
        </button>
        <div className="flex items-center gap-2 mt-0.5 px-1 flex-wrap">
          <span className="text-[10px] text-gray-400 dark:text-gray-500">{quantity}x</span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500">{activeDataCheckOption?.value === 'none' ? 'Kein Check' : activeDataCheckOption?.value === 'express' ? 'Express' : 'Standard'}</span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500">{isNumericDelivery ? `ca. ${dynamicDeliveryDays} Tage` : dynamicDeliveryDays}</span>
        </div>
      </div>
      )}

      <div className="border-t border-gray-200 dark:border-gray-600 my-2" />

      {/* Pricing Summary */}
      <div className="mb-3 p-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        {isStueckMode ? (
          <div className="flex justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-0.5"><span>Menge:</span><span>{quantity} Stück{supplierCode ? <span className="ml-1 px-1 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded text-[9px] font-bold">{supplierCode}</span> : null}<span className="ml-1 px-1 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 rounded text-[9px] font-bold">Stück</span></span></div>
        ) : (
          <div className="flex justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-0.5"><span>Fläche:</span><span>{areaM2.toFixed(2)} m²{supplierCode ? <span className="ml-1 px-1 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded text-[9px] font-bold">{supplierCode}</span> : null}{isFixedPrice ? <span className="ml-1 px-1 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 rounded text-[9px] font-bold">Festpreis</span> : null}</span></div>
        )}
        <div className="flex justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-0.5"><span>{isFixedPrice ? 'Festpreis:' : isStueckMode ? 'Preis/Stück:' : 'Grundpreis:'}</span><span>{basePrice.toFixed(2)} EUR</span></div>
        {finishingTotal > 0 && <div className="flex justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-0.5"><span>Veredelung:</span><span>+{finishingTotal.toFixed(2)} EUR</span></div>}
        {setupCost > 0 && <div className="flex justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-0.5"><span>Maschineneinrichtung:</span><span>+{setupCost.toFixed(2)} EUR</span></div>}
        {oversizeCost > 0 && <div className="flex justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-0.5"><span>Übergrößen-Aufschlag:</span><span>+{oversizeCost.toFixed(2)} EUR</span></div>}
        {packagingCost > 0 && <div className="flex justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-0.5"><span>Verpackung:</span><span>+{packagingCost.toFixed(2)} EUR</span></div>}
        {shippingCost > 0 && <div className="flex justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-0.5"><span>Versand:</span><span>+{shippingCost.toFixed(2)} EUR</span></div>}
        {dynamicDataCheckSurcharge > 0 && <div className="flex justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-0.5"><span>Datencheck:</span><span>+{dynamicDataCheckSurcharge.toFixed(2)} EUR</span></div>}
        {quantity > 1 && <div className="flex justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-0.5"><span>Menge:</span><span>x{quantity}</span></div>}
        <div className="border-t border-gray-200 dark:border-gray-600 mt-1.5 pt-1.5 flex justify-between">
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Gesamt:</span>
          <span className="text-sm font-bold text-primary-500">{pricingLoading ? '...' : dynamicGrandTotal.toFixed(2)} EUR</span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {canSave ? (
          <>
            <button data-guide-id="btn-save" onClick={onSave} className="selenny-btn-primary w-full text-xs">{designId ? 'DESIGN AKTUALISIEREN' : 'DESIGN SPEICHERN'}</button>
            {designId && onSaveAsNew && (
              <button onClick={onSaveAsNew} className="selenny-btn w-full text-xs bg-white border border-blue-600 text-blue-700 hover:bg-blue-50">ALS NEUES DESIGN SPEICHERN</button>
            )}
          </>
        ) : (
          <button disabled className="selenny-btn w-full text-xs bg-gray-400 text-gray-200 cursor-not-allowed" title="Bitte melde dich an um dein Design zu speichern">DESIGN SPEICHERN (Anmeldung erforderlich)</button>
        )}
        {isEmbedded && (
          <button data-guide-id="btn-cart" onClick={() => onAddToCart(onPricingChange?.current || { totalPrice: dynamicGrandTotal, basePrice, dataCheckSurcharge: dynamicDataCheckSurcharge, dataCheck, quantity, widthCm, heightCm })} className="selenny-btn w-full text-xs bg-green-600 text-white hover:bg-green-700">IN DEN WARENKORB</button>
        )}
        {isAdmin && (
          <button onClick={() => onAddToCart(onPricingChange?.current || { totalPrice: dynamicGrandTotal, basePrice, dataCheckSurcharge: dynamicDataCheckSurcharge, dataCheck, quantity, widthCm, heightCm })} className="selenny-btn w-full text-xs bg-blue-600 text-white hover:bg-blue-700">DRUCKDATEI EXPORTIEREN</button>
        )}
        {!isEmbedded && (
          <button onClick={() => { try { window.close(); } catch(e) {} setTimeout(() => window.history.back(), 100); }} className="selenny-btn w-full text-xs bg-gray-500 text-white hover:bg-gray-600">FENSTER SCHLIESSEN</button>
        )}
      </div>
      {designId && <p className="text-xs text-gray-400 mt-2 text-center">Design-ID: {designId}</p>}

      {/* ========== FLYOUT: Größe & Format (links / inline auf Mobile) ========== */}
      {activeFlyout === 'size' && (
        <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden ${isMobile ? 'relative z-[10] mt-2 mb-2' : 'fixed z-[100]'}`}
          style={isMobile ? {} : { right: 300, top: 80, width: 340, maxHeight: 'calc(100vh - 120px)' }}
        >
          <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Größe & Format</p>
            <button onClick={() => setActiveFlyout(null)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-lg leading-none">&times;</button>
          </div>
          <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>

            {/* Motivgröße */}
            <div className="mb-4">
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Motivgröße (cm)</label>
              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <label className="text-[10px] text-gray-400 mb-0.5 block">Breite</label>
                  <input type="text" inputMode="decimal" value={localWidth} onChange={(e) => setLocalWidth(e.target.value.replace(/[^0-9.,]/g, ''))} onFocus={(e) => setTimeout(() => e.target.select(), 0)} onBlur={applyDimensions} onKeyDown={(e) => e.key === 'Enter' && applyDimensions()} className="selenny-input" />
                </div>
                <span className="text-gray-400 text-sm mt-4">x</span>
                <div className="flex-1">
                  <label className="text-[10px] text-gray-400 mb-0.5 block">Höhe</label>
                  <input type="text" inputMode="decimal" value={localHeight} onChange={(e) => setLocalHeight(e.target.value.replace(/[^0-9.,]/g, ''))} onFocus={(e) => setTimeout(() => e.target.select(), 0)} onBlur={applyDimensions} onKeyDown={(e) => e.key === 'Enter' && applyDimensions()} className="selenny-input" />
                </div>
              </div>
            </div>

            {/* Standardgrößen */}
            {activeSizes.length > 0 && !hideSizes && (
            <div className="mb-4">
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Standardgrößen</label>
              <div className="grid grid-cols-2 gap-1.5">
                {activeSizes.map((s, i) => (
                  <button key={i} onClick={() => { setLocalWidth(String(s.w)); setLocalHeight(String(s.h)); setFixedPrice(s.fixedPrice || null); onDimensionChange(s.w, s.h); }}
                    className={`text-[11px] px-2 py-2 rounded-lg border-2 text-left transition-all ${
                      widthCm === s.w && heightCm === s.h
                        ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold'
                        : 'border-gray-100 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span>{s.label}</span>
                    {s.fixedPrice > 0 && <span className="block text-[9px] mt-0.5 text-green-600 dark:text-green-400 font-bold">{s.fixedPrice.toFixed(2)} EUR</span>}
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Randanschnitt — nur für nicht-embedded (Admin/Backend) anzeigen */}
            {!isEmbedded && (
            <div className="mb-2" data-guide-id="panel-bleed">
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 flex items-center justify-between">
                <span>Randanschnitt</span>
                <span className="text-gray-400 font-mono text-[11px]">{bleedCm} cm</span>
              </label>
              <input type="range" min="0" max="5" step="0.5" value={bleedCm} onChange={(e) => onBleedChange?.(Number(e.target.value))} className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-red-500" />
              <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                <span>0 cm</span><span>5 cm</span>
              </div>
            </div>
            )}
          </div>
        </div>
      )}

      {/* ========== FLYOUT: Bestelloptionen (links / inline auf Mobile) ========== */}
      {!HIDE_ORDER_OPTIONS && activeFlyout === 'order' && (
        <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden ${isMobile ? 'relative z-[10] mt-2 mb-2' : 'fixed z-[100]'}`}
          style={isMobile ? {} : { right: 300, top: 80, width: 340, maxHeight: 'calc(100vh - 120px)' }}
        >
          <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Bestelloptionen</p>
            <button onClick={() => setActiveFlyout(null)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-lg leading-none">&times;</button>
          </div>
          <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>

            {/* Menge */}
            <div className="mb-4">
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Menge</label>
              <div className="flex items-center gap-2">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="selenny-btn-secondary py-1.5 px-4 text-lg rounded-lg">-</button>
                <input type="number" value={quantity} onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))} className="selenny-input text-center w-20" min="1" />
                <button onClick={() => setQuantity(quantity + 1)} className="selenny-btn-secondary py-1.5 px-4 text-lg rounded-lg">+</button>
              </div>
            </div>

            {/* Material */}
            {printConfig && printConfig.materials?.length > 1 && (
              <div className="mb-4">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Material</label>
                <select value={selectedMaterialId || ''} onChange={(e) => setSelectedMaterialId(Number(e.target.value))} className="selenny-input">
                  {printConfig.materials.map(m => (
                    <option key={m.id} value={m.id}>{m.name}{m.weight_gsm ? ` (${m.weight_gsm} g/m²)` : ''}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Veredelung from API */}
            {printConfig && printConfig.finishing_options?.length > 0 && (
              <div className="mb-4">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Veredelung</label>
                <div className="space-y-1.5">
                  {printConfig.finishing_options.map(opt => (
                    <label key={opt.key} className="flex items-center gap-2.5 text-xs text-gray-700 dark:text-gray-300 cursor-pointer p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedFinishingKeys.includes(opt.key)}
                        onChange={(e) => {
                          setSelectedFinishingKeys(prev =>
                            e.target.checked ? [...prev, opt.key] : prev.filter(k => k !== opt.key)
                          );
                        }}
                        className="rounded border-gray-300"
                      />
                      <span className="flex-1">{opt.name}</span>
                      <span className="text-gray-400 text-[10px]">{opt.price.toFixed(2)} EUR/{opt.price_type === 'per_lm' ? 'lfm' : opt.price_type === 'per_piece' ? 'Stk' : opt.price_type === 'per_m2' ? 'm²' : 'pauschal'}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Datencheck */}
            <div className="mb-4">
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Datencheck</label>
              <div className="space-y-1.5">
                {activeDataCheckOptions.map(opt => (
                  <label key={opt.value} className={`flex items-center gap-2.5 text-xs cursor-pointer p-2.5 rounded-lg border-2 transition-all ${
                    dataCheck === opt.value ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'border-gray-100 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-200'
                  }`}>
                    <input type="radio" name="datacheck" value={opt.value} checked={dataCheck === opt.value} onChange={() => setDataCheck(opt.value)} className="accent-blue-500" />
                    <span className="flex-1">{opt.label}</span>
                    {opt.price > 0 && <span className="font-semibold">+{opt.price.toFixed(2)} EUR</span>}
                  </label>
                ))}
              </div>
            </div>

            {/* Lieferzeit */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <span className="text-xs text-gray-700 dark:text-gray-300">Lieferzeit: <span className="font-semibold text-gray-900 dark:text-white">{isNumericDelivery ? `ca. ${dynamicDeliveryDays} Werktage` : dynamicDeliveryDays}</span></span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== FLYOUT: Verarbeitung (links / inline auf Mobile) ========== */}
      {activeFlyout === 'finishing' && finishing && (
        <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden ${isMobile ? 'relative z-[10] mt-2 mb-2' : 'fixed z-[100]'}`}
          style={isMobile ? {} : { right: 300, top: 80, width: 420, maxHeight: 'calc(100vh - 120px)' }}
        >
          <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Verarbeitung</p>
            <button onClick={() => setActiveFlyout(null)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-lg leading-none">&times;</button>
          </div>

          <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>

            {/* Step 1: Mode */}
            <div className="mb-1">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Schritt 1 — Seitenauswahl</p>
              <ModeSelector
                mode={finishing.mode}
                onChange={(m) => { onFinishingChange?.({ ...finishing, mode: m }); setActiveSide(null); }}
                allowPerSide={!urlPricing || urlPricing.allowPerSideFinishing !== false}
              />
            </div>

            {/* Step 2: Finishing per side or all */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Schritt 2 — {finishing.mode === 'all' ? 'Verarbeitung (alle Seiten)' : 'Verarbeitung pro Seite'}
              </p>

              {finishing.mode === 'all' ? (
                <div className="space-y-3">
                  <FinishingTypeCards value={finishing.all.type} onChange={(type) => updateAllConfig({ ...finishing.all, type })} allowedTypes={urlPricing?.allowedFinishing} />
                  <FinishingOptions sideConfig={finishing.all} onChange={updateAllConfig} isAllMode urlPricing={urlPricing} />
                </div>
              ) : (
                <div className="space-y-2">
                  {['top', 'bottom', 'left', 'right'].map((side) => {
                    const cfg = finishing[side] || { type: 'cut', saumCm: 3, grommetMode: 'every50', grommetSize: 11 };
                    const ft = FINISHING_TYPES.find(f => f.id === cfg.type);
                    const isOpen = activeSide === side;

                    return (
                      <div key={side} className={`rounded-lg border-2 transition-all ${isOpen ? 'border-blue-400 bg-blue-50/30' : 'border-gray-200'}`}>
                        <button
                          onClick={() => setActiveSide(isOpen ? null : side)}
                          className="w-full flex items-center justify-between px-3 py-2 text-left"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-700 w-12">{SIDE_LABELS[side]}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                              cfg.type === 'cut' ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-700'
                            }`}>{ft?.label || 'Schneiden'}</span>
                          </div>
                          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                        </button>

                        {isOpen && (
                          <div className="px-3 pb-3 animate-fadeIn">
                            <FinishingTypeCards value={cfg.type} onChange={(type) => updateSideConfig(side, { ...cfg, type })} allowedTypes={urlPricing?.allowedFinishing} />
                            <FinishingOptions sideConfig={cfg} onChange={(c) => updateSideConfig(side, c)} urlPricing={urlPricing} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="text-[10px] text-gray-400 flex flex-wrap gap-2.5 pt-3 mt-4 border-t border-gray-100">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400 inline-block" /> Öse</span>
              <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ borderBottom: '1.5px dashed #ef4444' }} /> Beschnitt</span>
              <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ borderBottom: '1.5px dashed #3b82f6' }} /> Saum</span>
              <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ borderBottom: '2px dashed #7c3aed' }} /> Hohlsaum</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductPanel;
